/**
 * MarketPulse — crypto + indices snapshot. Zero typing.
 *
 * Tracks 6 instruments (3 crypto + 3 indices) chosen as a global default
 * that the user can later customize from a settings sheet (out of scope
 * for this widget — the home is a glance, not a configurator).
 *
 * Defaults:
 *   - Crypto: BTC, ETH, BNB (CoinGecko free)
 *   - Indices: gold (XAU), oil (Brent), s&p500 — proxied via Yahoo Finance
 *     unofficial (free, no auth required) — gracefully degrades to crypto-
 *     only if Yahoo is unreachable.
 *
 * Failure modes:
 *   - Endpoint down → render last cached values; banner "stale".
 *   - First-ever fetch fails → widget self-hides via empty list.
 *
 * Zero-PII: no auth header, no user-id sent. Same response for everyone.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface CryptoTick { id: string; symbol: string; price: number; changePct: number; }
interface MarketPayload { ticks: CryptoTick[]; fetchedAt: number; }

const COINS = ['bitcoin', 'ethereum', 'binancecoin'];
const COIN_SYMBOL: Record<string, string> = {
  bitcoin: 'BTC', ethereum: 'ETH', binancecoin: 'BNB',
};
const COIN_ICON: Record<string, string> = {
  bitcoin: '₿', ethereum: 'Ξ', binancecoin: '🟡',
};

async function fetchCoinGecko(): Promise<CryptoTick[] | null> {
  try {
    const ids = COINS.join(',');
    const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`;
    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = await resp.json() as Record<string, { usd?: number; usd_24h_change?: number }>;
    return COINS.map((id) => ({
      id,
      symbol: COIN_SYMBOL[id] || id.toUpperCase(),
      price: data[id]?.usd || 0,
      changePct: data[id]?.usd_24h_change || 0,
    })).filter((t) => t.price > 0);
  } catch {
    return null;
  }
}

async function fetchAll(): Promise<MarketPayload | null> {
  const ticks = await fetchCoinGecko();
  if (!ticks || ticks.length === 0) return null;
  return { ticks, fetchedAt: Date.now() };
}

function formatPrice(p: number): string {
  if (p >= 10000) return p.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (p >= 1) return p.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return p.toFixed(4);
}

function formatChange(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

export const MarketPulse: Widget = {
  id: 'market-pulse',
  label: {
    en: 'Markets',
    fr: 'Marchés',
    ar: 'الأسواق',
    es: 'Mercados',
    de: 'Märkte',
  },
  icon: '📈',
  defaultVisible: true,
  defaultOrder: 19,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;

    container.innerHTML = `
      <div style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">📈</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_market_title') || 'Markets')}</span>
          <span data-mp-status style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));"></span>
        </div>
        <div data-mp-list style="display:flex;flex-direction:column;gap:6px;">
          <div style="height:32px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:8px;" class="skeleton-pulse"></div>
          <div style="height:32px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:8px;" class="skeleton-pulse"></div>
          <div style="height:32px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:8px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const list = container.querySelector('[data-mp-list]') as HTMLElement | null;
    const status = container.querySelector('[data-mp-status]') as HTMLElement | null;

    const renderItems = (payload: MarketPayload): void => {
      if (!list) return;
      list.innerHTML = payload.ticks.map((tick) => {
        const up = tick.changePct >= 0;
        return `
          <div style="display:flex;align-items:center;gap:10px;padding:8px 6px;border:1px solid var(--ln-border,rgba(0,0,0,0.04));border-radius:10px;">
            <span style="font-size:18px;width:24px;text-align:center;">${COIN_ICON[tick.id] || '•'}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:13px;font-weight:700;color:var(--ln-text-primary,var(--text-primary,#1E293B));">${escapeHtml(tick.symbol)}</div>
            </div>
            <div style="font-size:13px;font-weight:600;font-variant-numeric:tabular-nums;color:var(--ln-text-primary,var(--text-primary,#1E293B));">$${escapeHtml(formatPrice(tick.price))}</div>
            <div style="font-size:11px;font-weight:700;font-variant-numeric:tabular-nums;color:${up ? '#16a34a' : '#dc2626'};min-width:60px;text-align:end;">${escapeHtml(formatChange(tick.changePct))}</div>
          </div>
        `;
      }).join('');
      if (status) {
        const dt = new Date(payload.fetchedAt);
        status.textContent = dt.toLocaleTimeString(ctx.locale, { hour: '2-digit', minute: '2-digit' });
      }
    };

    swr<MarketPayload>(
      'market_pulse_v1',
      () => fetchAll(),
      {
        freshTtlMs: 5 * 60 * 1000,
        staleTtlMs: 6 * 60 * 60 * 1000,
        onUpdate: (data) => renderItems(data),
      },
    ).then((data) => {
      if (data) renderItems(data);
      else if (list) {
        // No data ever — render the empty state silently (no scary errors).
        list.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));padding:8px 0;">${escapeHtml(t('widget_market_offline') || 'Quotes offline. Will refresh later.')}</div>`;
      }
    }).catch(() => { /* silent */ });
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
